import { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Dialog, Flex, Grid, SegmentedControl, Select, Text, TextArea, TextField } from "@radix-ui/themes";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { useEntities } from "@/hooks/useCommonQueries";
import {
  CreateDashboardWidgetRequest,
  DashboardWidget,
  WidgetType,
} from "@/types/breeze/Dashboard";
import { FieldMultiSelect } from "./FieldMultiSelect";
import PaginatedFileGrid from "@/components/files/PaginatedFileGrid";
import FileDisplay from "@/components/files/FileDisplay";
import { File as StoredFile } from "@/types/breeze/File";
import ChartBuilderModal from "./ChartBuilderModal";
import { useQuery } from "@tanstack/react-query";
import { chartConfigurationService } from "@/services/breeze/ChartConfigurationService";
import { ChartConfiguration } from "@/types/breeze/ChartConfiguration";
import {
  BarChart3,
  List as ListIcon,
  Zap,
  StickyNote,
  PlayCircle,
  Rocket,
  ArrowLeft,
  Table,
  LucideIcon,
} from "lucide-react";
import MaterialisedViewBuilderModal from "./MaterialisedViewBuilderModal";

// System columns that AnyRepository always returns, even if the tenant hasn't
// defined them as user fields.
const SYSTEM_FIELDS = ["id", "created_at", "updated_at"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (widget: CreateDashboardWidgetRequest) => void;
  /** When provided, the modal prefills its fields and acts as an editor. */
  existing?: DashboardWidget | null;
}

interface WidgetTypeOption {
  value: WidgetType;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
}

const WIDGET_TYPE_OPTIONS: WidgetTypeOption[] = [
  { value: "chart", label: "Chart", description: "Pick a saved chart, or design a new one", icon: BarChart3, color: "blue" },
  { value: "materialised_view", label: "Materialised view", description: "Table combining columns from multiple sources", icon: Table, color: "iris" },
  { value: "list", label: "List", description: "Recent rows from an entity", icon: ListIcon, color: "green" },
  { value: "quick_action", label: "Quick action", description: "A button that navigates somewhere", icon: Zap, color: "amber" },
  { value: "markdown", label: "Note", description: "Free-form rich text", icon: StickyNote, color: "purple" },
  { value: "media", label: "Media", description: "Video or audio from storage or a URL", icon: PlayCircle, color: "pink" },
  { value: "getting_started", label: "Getting started", description: "Onboarding CTA card", icon: Rocket, color: "cyan" },
];

const QUICK_ACTION_STATIC_ROUTES: { value: string; label: string }[] = [
  { value: "/settings/data-model", label: "Data Models" },
  { value: "/workflows", label: "Workflows" },
  { value: "/settings/users", label: "User Management" },
  { value: "/settings", label: "Settings" },
  { value: "/integrations/anythinkpay", label: "AnythinkPay" },
  { value: "/api-docs", label: "API Documentation" },
  { value: "/files", label: "Files" },
];

export function AddWidgetModal({ open, onOpenChange, onSubmit, existing }: Props) {
  const isEditing = !!existing;
  const [type, setType] = useState<WidgetType>("chart");
  const [title, setTitle] = useState("");
  const [browsingTypes, setBrowsingTypes] = useState(true);

  // Type-specific state
  const [entityName, setEntityName] = useState("");
  const [listFields, setListFields] = useState<string[]>(["id", "name", "created_at"]);
  const [listLimit, setListLimit] = useState("5");
  const [listSortField, setListSortField] = useState("created_at");
  const [listSortDir, setListSortDir] = useState<"asc" | "desc">("desc");
  const [actionLabel, setActionLabel] = useState("Open");
  const [actionLinkType, setActionLinkType] = useState<"internal" | "external">("internal");
  const [actionRoute, setActionRoute] = useState("/settings");
  const [actionExternalUrl, setActionExternalUrl] = useState("");
  const [actionTarget, setActionTarget] = useState<"_blank" | "_self">("_blank");
  const [actionDescription, setActionDescription] = useState("");
  const [markdownBody, setMarkdownBody] = useState("");

  const [mediaSource, setMediaSource] = useState<"file" | "external">("external");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaFile, setMediaFile] = useState<StoredFile | null>(null);

  const [chartId, setChartId] = useState("");
  const [chartBuilderOpen, setChartBuilderOpen] = useState(false);
  const [materialisedViewBuilderOpen, setMaterialisedViewBuilderOpen] = useState(false);

  type GsAction = { label: string; linkType: "internal" | "external"; route: string; url: string };
  const [gsBody, setGsBody] = useState("");
  const [gsActions, setGsActions] = useState<GsAction[]>([
    { label: "Create Data Model", linkType: "internal", route: "/settings/data-model", url: "" },
    { label: "View Anythink Docs", linkType: "external", route: "/settings", url: "https://docs.anythink.cloud" },
  ]);

  const { data: charts = [] } = useQuery<ChartConfiguration[]>({
    queryKey: ["charts-all-for-dashboard"],
    queryFn: () => chartConfigurationService.getChartConfigurations(),
    enabled: type === "chart",
  });

  const chartOptions = useMemo(
    () =>
      charts.map((c) => ({
        value: String(c.id),
        label: c.entityName ? `${c.name} (${c.entityName})` : c.name,
      })),
    [charts],
  );

  const { data: entities = [] } = useEntities();
  const entityOptions = useMemo(
    () =>
      entities
        .filter((e) => !e.is_junction)
        .map((e) => ({ value: e.name, label: e.name })),
    [entities],
  );

  const quickActionRouteOptions = useMemo(() => {
    const entityRoutes = entities
      .filter((e) => !e.is_junction)
      .map((e) => ({ value: `/entities/${e.name}`, label: `Entity: ${e.name}` }));
    return [...entityRoutes, ...QUICK_ACTION_STATIC_ROUTES];
  }, [entities]);


  const selectedEntity = useMemo(
    () => entities.find((e) => e.name === entityName),
    [entities, entityName],
  );

  // Columns available to display + sort. Merges system columns with the tenant's
  // configured standard fields (skips relationship fields — they don't live on
  // the same row).
  const fieldOptions = useMemo(() => {
    if (!selectedEntity) {
      return SYSTEM_FIELDS.map((f) => ({ value: f, label: f }));
    }
    const userFields = selectedEntity.fields
      .filter((f) => !f.relationship_id)
      .map((f) => f.name);
    const merged = Array.from(new Set([...SYSTEM_FIELDS, ...userFields]));
    return merged.map((f) => ({ value: f, label: f }));
  }, [selectedEntity]);

  // If the user changes entity, drop any field selections that no longer exist
  useEffect(() => {
    if (!selectedEntity) return;
    const available = new Set(fieldOptions.map((o) => o.value));
    setListFields((prev) => {
      const next = prev.filter((f) => available.has(f));
      return next.length > 0 ? next : ["id"];
    });
    setListSortField((prev) => (available.has(prev) ? prev : "created_at"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName]);

  useEffect(() => {
    if (isEditing) return;
    if (title.trim()) return;
    if (type === "chart") return;
    const opt = WIDGET_TYPE_OPTIONS.find((o) => o.value === type);
    if (opt) setTitle(opt.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Sync from `existing` when opening in edit mode; reset when closing.
  useEffect(() => {
    if (!open) {
      setType("chart");
      setTitle("");
      setBrowsingTypes(true);
      setEntityName("");
      setListFields(["id", "name", "created_at"]);
      setListLimit("5");
      setListSortField("created_at");
      setListSortDir("desc");
      setActionLabel("Open");
      setActionLinkType("internal");
      setActionRoute("/settings");
      setActionExternalUrl("");
      setActionTarget("_blank");
      setActionDescription("");
      setMarkdownBody("");
      setMediaSource("external");
      setMediaUrl("");
      setMediaFile(null);
      setChartId("");
      setChartBuilderOpen(false);
      setGsBody("");
      setGsActions([
        { label: "Create Data Model", linkType: "internal", route: "/settings/data-model", url: "" },
        { label: "View Anythink Docs", linkType: "external", route: "/settings", url: "https://docs.anythink.cloud" },
      ]);
      return;
    }

    setBrowsingTypes(!existing);

    if (existing) {
      const cfg = safeParse(existing.config_json);
      const str = (k: string, fallback: string): string => {
        const v = cfg?.[k];
        return v == null ? fallback : String(v as string | number);
      };
      setType(existing.type);
      setTitle(existing.title);
      if (existing.type === "list") {
        setEntityName(str("entityName", ""));
        const fieldsRaw = str("fields", "id,name,created_at");
        setListFields(
          fieldsRaw
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean),
        );
        setListLimit(str("limit", "5"));
        const sortRaw = str("sort", "-created_at");
        setListSortDir(sortRaw.startsWith("-") ? "desc" : "asc");
        setListSortField(sortRaw.replace(/^-/, "") || "created_at");
      }
      if (existing.type === "quick_action") {
        setActionLabel(str("label", "Open"));
        const url = str("url", "");
        if (url) {
          setActionLinkType("external");
          setActionExternalUrl(url);
          const target = str("target", "_blank");
          setActionTarget(target === "_self" ? "_self" : "_blank");
        } else {
          setActionLinkType("internal");
          setActionRoute(str("route", "/settings"));
        }
        setActionDescription(str("description", ""));
      }
      if (existing.type === "markdown") setMarkdownBody(str("body", ""));
      if (existing.type === "media") {
        const source = str("source", "external") as "file" | "external";
        setMediaSource(source);
        setMediaUrl(str("url", ""));
        const fid = Number(str("fileId", "0"));
        if (fid > 0) {
          const name = str("fileName", `File ${fid}`);
          setMediaFile({
            id: fid,
            original_file_name: name,
            file_name: name,
            file_type: str("fileType", ""),
            file_size: 0,
            storage_key: "",
            is_public: false,
            anythink_url: "",
            cdn_url: "",
          } as StoredFile);
        }
      }
      if (existing.type === "chart") {
        setChartId(str("chartId", ""));
      }
      if (existing.type === "getting_started") {
        setGsBody(str("body", ""));
        const rawActions = cfg?.actions;
        if (Array.isArray(rawActions) && rawActions.length > 0) {
          setGsActions(rawActions.map((raw) => {
            const a = raw as Record<string, unknown>;
            const url = typeof a?.url === "string" ? a.url : "";
            const route = typeof a?.route === "string" ? a.route : "/settings";
            return {
              label: typeof a?.label === "string" ? a.label : "",
              linkType: url ? "external" : "internal",
              url,
              route,
            };
          }));
        }
      }
    }
  }, [open, existing]);

  const buildConfig = (): string | null => {
    switch (type) {
      case "list":
        return JSON.stringify({
          entityName,
          fields: listFields.join(","),
          limit: Number(listLimit) || 5,
          sort: (listSortDir === "desc" ? "-" : "") + listSortField,
        });
      case "quick_action":
        return JSON.stringify({
          label: actionLabel,
          ...(actionLinkType === "external"
            ? { url: actionExternalUrl, target: actionTarget }
            : { route: actionRoute }),
          description: actionDescription || undefined,
        });
      case "chart":
        return JSON.stringify({ chartId: Number(chartId) || 0 });
      case "markdown":
        return JSON.stringify({ body: markdownBody });
      case "media":
        return JSON.stringify(
          mediaSource === "file"
            ? {
                source: "file",
                fileId: mediaFile?.id ?? 0,
                fileName: mediaFile?.original_file_name || mediaFile?.file_name || undefined,
                fileType: mediaFile?.file_type || undefined,
              }
            : { source: "external", url: mediaUrl },
        );
      case "getting_started":
        return JSON.stringify({
          body: gsBody || undefined,
          actions: gsActions
            .filter((a) => a.label.trim() && (a.linkType === "external" ? a.url.trim() : a.route))
            .map((a) => a.linkType === "external"
              ? { label: a.label.trim(), url: a.url.trim() }
              : { label: a.label.trim(), route: a.route }),
        });
      default:
        return null;
    }
  };

  const selectedChart = useMemo(
    () => charts.find((c) => String(c.id) === chartId) ?? null,
    [charts, chartId],
  );

  const canSubmit = (() => {
    if (type === "chart") return !!chartId && Number(chartId) > 0;
    if (!title.trim()) return false;
    if (type === "list") return !!entityName && listFields.length > 0;
    if (type === "quick_action") {
      return actionLinkType === "external"
        ? !!actionExternalUrl.trim()
        : !!actionRoute;
    }
    if (type === "media") {
      return mediaSource === "file"
        ? !!mediaFile
        : !!mediaUrl.trim();
    }
    return true;
  })();

  const handleSubmit = () => {
    const effectiveTitle = type === "chart"
      ? (selectedChart?.name?.trim() || "Chart")
      : title.trim();
    onSubmit({
      type,
      title: effectiveTitle,
      config_json: buildConfig(),
      sort_order: existing?.sort_order ?? 0,
    });
    onOpenChange(false);
  };

  // When a child modal is open, block outside-click dismiss on this one.
  // Without this, clicks inside the child modal (e.g. on a SearchableSelect
  // Popover portaled to document.body) fire AddWidgetModal's outside-click
  // handler and unmount the whole tree — including the child modal.
  const childOpen = chartBuilderOpen || materialisedViewBuilderOpen;

  const selectedTypeOption = WIDGET_TYPE_OPTIONS.find((o) => o.value === type);

  const handleTypeSelect = (next: WidgetType) => {
    setType(next);
    setBrowsingTypes(false);
    if (next === "materialised_view") setMaterialisedViewBuilderOpen(true);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="640px"
        onPointerDownOutside={childOpen ? (e) => e.preventDefault() : undefined}
        onInteractOutside={childOpen ? (e) => e.preventDefault() : undefined}
      >
        <Dialog.Title>{isEditing ? "Edit widget" : "Add widget"}</Dialog.Title>
        <Dialog.Description size="2" mb="4" color="gray">
          {isEditing
            ? "Update this widget's type, title or configuration."
            : browsingTypes
              ? "Pick a widget type to get started."
              : "Configure your widget. You can adjust these later."}
        </Dialog.Description>

        {browsingTypes ? (
          <Grid columns="3" gap="3" width="100%">
            {WIDGET_TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = opt.value === type;
              return (
                <Card
                  key={opt.value}
                  onClick={() => handleTypeSelect(opt.value)}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    border: selected
                      ? `2px solid var(--${opt.color}-9)`
                      : "1px solid var(--gray-6)",
                    backgroundColor: selected
                      ? `var(--${opt.color}-4)`
                      : "var(--color-panel-solid)",
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "var(--gray-3)";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "var(--color-panel-solid)";
                  }}
                >
                  <Flex direction="column" align="center" gap="2" p="3">
                    <Box
                      style={{
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: `var(--${opt.color}-4)`,
                      }}
                    >
                      <Icon size={20} color={`var(--${opt.color}-11)`} />
                    </Box>
                    <Box style={{ textAlign: "center" }}>
                      <Text size="2" weight="medium" style={{ display: "block" }}>
                        {opt.label}
                      </Text>
                      <Text size="1" color="gray" style={{ display: "block", marginTop: 2 }}>
                        {opt.description}
                      </Text>
                    </Box>
                  </Flex>
                </Card>
              );
            })}
          </Grid>
        ) : (
        <Flex direction="column" gap="4">
          {selectedTypeOption && (
            <Card
              style={{
                border: `2px solid var(--${selectedTypeOption.color}-9)`,
                backgroundColor: `var(--${selectedTypeOption.color}-2)`,
              }}
            >
              <Flex justify="between" align="center" p="2">
                <Flex gap="3" align="center">
                  <Box
                    style={{
                      padding: 6,
                      borderRadius: 8,
                      backgroundColor: `var(--${selectedTypeOption.color}-9)`,
                      display: "flex",
                    }}
                  >
                    <selectedTypeOption.icon size={16} color="white" />
                  </Box>
                  <Box>
                    <Text size="2" weight="bold" style={{ display: "block" }}>
                      {selectedTypeOption.label}
                    </Text>
                    <Text size="1" color="gray" style={{ display: "block" }}>
                      {selectedTypeOption.description}
                    </Text>
                  </Box>
                </Flex>
                {!isEditing && (
                  <Button variant="soft" size="1" onClick={() => setBrowsingTypes(true)}>
                    <ArrowLeft size={12} /> Change
                  </Button>
                )}
              </Flex>
            </Card>
          )}

          {type !== "chart" && (
            <Box>
              <Text size="2" weight="medium" mb="1" asChild>
                <label>Title</label>
              </Text>
              <TextField.Root
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Monthly active users"
              />
            </Box>
          )}

          {type === "list" && (
            <Box>
              <Text size="2" weight="medium" mb="1" asChild>
                <label>Entity</label>
              </Text>
              <SearchableSelect
                value={entityName}
                onChange={setEntityName}
                options={entityOptions}
                placeholder="Select an entity"
                searchPlaceholder="Search entities..."
                width={472}
              />
            </Box>
          )}

          {type === "list" && (
            <>
              <Box>
                <Text size="2" weight="medium" mb="1" asChild>
                  <label>Fields</label>
                </Text>
                <FieldMultiSelect
                  value={listFields}
                  onChange={setListFields}
                  options={fieldOptions}
                  placeholder={entityName ? "Select fields..." : "Select an entity first"}
                  disabled={!entityName}
                />
              </Box>
              <Flex gap="3">
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1" asChild>
                    <label>Row limit</label>
                  </Text>
                  <TextField.Root
                    type="number"
                    value={listLimit}
                    onChange={(e) => setListLimit(e.target.value)}
                    min={1}
                    max={50}
                  />
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1" asChild>
                    <label>Sort by</label>
                  </Text>
                  <Select.Root
                    value={listSortField}
                    onValueChange={setListSortField}
                    disabled={!entityName}
                  >
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      {fieldOptions.map((f) => (
                        <Select.Item key={f.value} value={f.value}>
                          {f.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1" asChild>
                    <label>Direction</label>
                  </Text>
                  <Select.Root
                    value={listSortDir}
                    onValueChange={(v) => setListSortDir(v as "asc" | "desc")}
                  >
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      <Select.Item value="desc">Descending</Select.Item>
                      <Select.Item value="asc">Ascending</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Box>
              </Flex>
            </>
          )}

          {type === "quick_action" && (
            <>
              <Box>
                <Text size="2" weight="medium" mb="1" asChild>
                  <label>Link type</label>
                </Text>
                <Select.Root
                  value={actionLinkType}
                  onValueChange={(v) => setActionLinkType(v as "internal" | "external")}
                >
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content>
                    <Select.Item value="internal">Internal page</Select.Item>
                    <Select.Item value="external">External URL</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Box>
              {actionLinkType === "internal" ? (
                <Box>
                  <Text size="2" weight="medium" mb="1" asChild>
                    <label>Destination</label>
                  </Text>
                  <SearchableSelect
                    value={actionRoute}
                    onChange={setActionRoute}
                    options={quickActionRouteOptions}
                    placeholder="Select a destination"
                    searchPlaceholder="Search destinations..."
                    width={472}
                  />
                </Box>
              ) : (
                <>
                  <Box>
                    <Text size="2" weight="medium" mb="1" asChild>
                      <label>URL</label>
                    </Text>
                    <TextField.Root
                      value={actionExternalUrl}
                      onChange={(e) => setActionExternalUrl(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </Box>
                  <Box>
                    <Text size="2" weight="medium" mb="1" asChild>
                      <label>Open in</label>
                    </Text>
                    <Select.Root
                      value={actionTarget}
                      onValueChange={(v) => setActionTarget(v as "_blank" | "_self")}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        <Select.Item value="_blank">New tab</Select.Item>
                        <Select.Item value="_self">Current tab</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Box>
                </>
              )}
              <Box>
                <Text size="2" weight="medium" mb="1" asChild>
                  <label>Description (optional)</label>
                </Text>
                <TextField.Root
                  value={actionDescription}
                  onChange={(e) => setActionDescription(e.target.value)}
                  placeholder="What does this shortcut do?"
                />
              </Box>
              <Box>
                <Text size="2" weight="medium" mb="1" asChild>
                  <label>Button label</label>
                </Text>
                <TextField.Root
                  value={actionLabel}
                  onChange={(e) => setActionLabel(e.target.value)}
                  placeholder="Open"
                />
              </Box>
            </>
          )}

          {type === "chart" && (
            <>
              <Button
                type="button"
                size="3"
                onClick={() => setChartBuilderOpen(true)}
                style={{ justifyContent: "center" }}
              >
                <BarChart3 size={18} />
                Design a new chart
              </Button>
              <Text size="1" color="gray" style={{ marginTop: -8 }} asChild>
                <div>Opens the chart builder with a live preview. Saved charts join your chart library.</div>
              </Text>

              {chartOptions.length > 0 && (
                <Box>
                  <Text size="2" weight="medium" mb="1" color="gray" asChild>
                    <label>Or pick from {chartOptions.length} saved chart{chartOptions.length === 1 ? "" : "s"}</label>
                  </Text>
                  <SearchableSelect
                    value={chartId}
                    onChange={setChartId}
                    options={chartOptions}
                    placeholder="Select a saved chart"
                    searchPlaceholder="Search charts..."
                    width={472}
                  />
                </Box>
              )}
            </>
          )}

          {type === "markdown" && (
            <Box>
              <Text size="2" weight="medium" mb="1" asChild>
                <label>Content</label>
              </Text>
              <TextArea
                value={markdownBody}
                onChange={(e) => setMarkdownBody(e.target.value)}
                placeholder="Team announcements, pinned links, etc."
                rows={6}
              />
            </Box>
          )}

          {type === "getting_started" && (
            <>
              <Box>
                <Text size="2" weight="medium" mb="1" asChild>
                  <label>Body</label>
                </Text>
                <TextArea
                  value={gsBody}
                  onChange={(e) => setGsBody(e.target.value)}
                  placeholder="Introductory copy that greets your users..."
                  rows={4}
                />
              </Box>
              <Box>
                <Flex justify="between" align="center" mb="2">
                  <Text size="2" weight="medium" asChild>
                    <label>Actions</label>
                  </Text>
                  {gsActions.length < 4 && (
                    <Button
                      size="1"
                      variant="soft"
                      type="button"
                      onClick={() => setGsActions((prev) => [...prev, { label: "", linkType: "internal", route: "/settings", url: "" }])}
                    >
                      + Add action
                    </Button>
                  )}
                </Flex>
                <Flex direction="column" gap="2">
                  {gsActions.map((action, i) => (
                    <Flex key={i} gap="2" align="start">
                      <Box style={{ flex: "1 1 30%" }}>
                        <TextField.Root
                          value={action.label}
                          onChange={(e) => setGsActions((prev) => prev.map((a, j) => j === i ? { ...a, label: e.target.value } : a))}
                          placeholder="Button label"
                        />
                      </Box>
                      <Box style={{ flex: "0 0 120px" }}>
                        <Select.Root
                          value={action.linkType}
                          onValueChange={(v) => setGsActions((prev) => prev.map((a, j) => j === i ? { ...a, linkType: v as "internal" | "external" } : a))}
                        >
                          <Select.Trigger style={{ width: "100%" }} />
                          <Select.Content>
                            <Select.Item value="internal">Internal</Select.Item>
                            <Select.Item value="external">External</Select.Item>
                          </Select.Content>
                        </Select.Root>
                      </Box>
                      <Box style={{ flex: "1 1 40%" }}>
                        {action.linkType === "internal" ? (
                          <SearchableSelect
                            value={action.route}
                            onChange={(v) => setGsActions((prev) => prev.map((a, j) => j === i ? { ...a, route: v } : a))}
                            options={quickActionRouteOptions}
                            placeholder="Destination"
                            searchPlaceholder="Search..."
                            width={220}
                          />
                        ) : (
                          <TextField.Root
                            value={action.url}
                            onChange={(e) => setGsActions((prev) => prev.map((a, j) => j === i ? { ...a, url: e.target.value } : a))}
                            placeholder="https://..."
                          />
                        )}
                      </Box>
                      <Button
                        size="1"
                        variant="soft"
                        color="red"
                        type="button"
                        onClick={() => setGsActions((prev) => prev.filter((_, j) => j !== i))}
                        disabled={gsActions.length === 1}
                      >
                        Remove
                      </Button>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            </>
          )}

          {type === "media" && (
            <>
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium" asChild>
                  <label>Source</label>
                </Text>
                <SegmentedControl.Root
                  value={mediaSource}
                  onValueChange={(v) => setMediaSource(v as "file" | "external")}
                >
                  <SegmentedControl.Item value="file">Storage</SegmentedControl.Item>
                  <SegmentedControl.Item value="external">External URL</SegmentedControl.Item>
                </SegmentedControl.Root>
              </Flex>
              {mediaSource === "external" ? (
                <Box>
                  <Text size="2" weight="medium" mb="1" asChild>
                    <label>URL</label>
                  </Text>
                  <TextField.Root
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <Text size="1" color="gray" mt="1" asChild>
                    <div>YouTube, Vimeo and Loom URLs embed automatically. Direct .mp4/.mp3 URLs render as a player.</div>
                  </Text>
                </Box>
              ) : mediaFile ? (
                <Flex direction="column" gap="2">
                  <Flex justify="between" align="center">
                    <Text size="2" weight="medium" asChild>
                      <label>Selected file</label>
                    </Text>
                    <Button type="button" variant="soft" size="1" onClick={() => setMediaFile(null)}>
                      Change
                    </Button>
                  </Flex>
                  <FileDisplay files={[mediaFile]} />
                </Flex>
              ) : (
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium" asChild>
                    <label>Pick a file</label>
                  </Text>
                  <Box style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--gray-5)", borderRadius: "var(--radius-3)", padding: 8 }}>
                    <PaginatedFileGrid
                      pageSize={12}
                      enableSelection
                      multiple={false}
                      maxFiles={1}
                      selectedFiles={[]}
                      compact
                      showActions={false}
                      onSelectionChanged={(files: StoredFile[]) => {
                        if (files[0]) setMediaFile(files[0]);
                      }}
                    />
                  </Box>
                </Flex>
              )}
            </>
          )}
        </Flex>
        )}

        <Flex gap="2" mt="5" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          {!browsingTypes && (
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {isEditing ? "Save" : "Add widget"}
            </Button>
          )}
        </Flex>
      </Dialog.Content>

      <ChartBuilderModal
        open={chartBuilderOpen}
        onOpenChange={setChartBuilderOpen}
        onSaved={(chart) => {
          if (!chart.id) return;
          // Auto-commit — title from chart.name because the charts query hasn't refetched yet, so the picker would otherwise show the id.
          onSubmit({
            type: "chart",
            title: chart.name?.trim() || title.trim() || "Chart",
            config_json: JSON.stringify({ chartId: chart.id }),
            sort_order: existing?.sort_order ?? 0,
          });
          onOpenChange(false);
        }}
      />

      <MaterialisedViewBuilderModal
        open={materialisedViewBuilderOpen}
        onOpenChange={setMaterialisedViewBuilderOpen}
        onSaved={(view) => {
          if (!view.id) return;
          onSubmit({
            type: "materialised_view",
            title: view.name?.trim() || title.trim() || "Materialised view",
            config_json: JSON.stringify({ viewId: view.id }),
            sort_order: existing?.sort_order ?? 0,
          });
          onOpenChange(false);
        }}
      />
    </Dialog.Root>
  );
}

function safeParse(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
